import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type NotificationLevel = 'success' | 'error' | 'info';

export interface AppNotification {
  id: string;
  message: string;
  level: NotificationLevel;
  createdAt: number;
  ttlMs: number;
  repeatCount: number;
}

@Injectable({ providedIn: 'root' })
export class NotificationService implements OnDestroy {
  private readonly maxVisible = 5;
  private readonly defaultTtlMs = 7000;
  private readonly dedupeWindowMs = 1200;
  private readonly notificationsSubject = new BehaviorSubject<AppNotification[]>([]);
  private readonly timeoutById = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly dueAtById = new Map<string, number>();
  private readonly remainingMsById = new Map<string, number>();

  readonly notifications$ = this.notificationsSubject.asObservable();

  success(message: string, ttlMs?: number): void {
    this.push(message, 'success', ttlMs);
  }

  error(message: string, ttlMs?: number): void {
    this.push(message, 'error', ttlMs);
  }

  info(message: string, ttlMs?: number): void {
    this.push(message, 'info', ttlMs);
  }

  remove(id: string): void {
    this.clearTimer(id);
    this.dueAtById.delete(id);
    this.remainingMsById.delete(id);
    const next = this.notificationsSubject.value.filter((n) => n.id !== id);
    this.notificationsSubject.next(next);
  }

  pause(id: string): void {
    const dueAt = this.dueAtById.get(id);
    if (!dueAt) return;
    const remaining = Math.max(0, dueAt - Date.now());
    this.remainingMsById.set(id, remaining);
    this.clearTimer(id);
    this.dueAtById.delete(id);
  }

  resume(id: string): void {
    const remaining = this.remainingMsById.get(id);
    if (remaining === undefined) return;
    this.remainingMsById.delete(id);
    if (remaining <= 0) {
      this.remove(id);
      return;
    }
    this.startTimer(id, remaining);
  }

  ngOnDestroy(): void {
    for (const timeout of this.timeoutById.values()) {
      clearTimeout(timeout);
    }
    this.timeoutById.clear();
    this.dueAtById.clear();
    this.remainingMsById.clear();
  }

  private push(messageRaw: string, level: NotificationLevel, ttlMs?: number): void {
    const message = (messageRaw || '').trim();
    if (!message) return;

    const now = Date.now();
    const current = this.notificationsSubject.value;
    const latest = current[0];
    if (
      latest &&
      latest.level === level &&
      latest.message === message &&
      now - latest.createdAt <= this.dedupeWindowMs
    ) {
      const merged: AppNotification = {
        ...latest,
        createdAt: now,
        repeatCount: latest.repeatCount + 1,
        ttlMs: this.resolveTtl(ttlMs)
      };
      const next = [merged, ...current.slice(1)];
      this.notificationsSubject.next(next);
      this.startTimer(merged.id, merged.ttlMs);
      return;
    }

    const notification: AppNotification = {
      id: this.newId(),
      message,
      level,
      createdAt: now,
      ttlMs: this.resolveTtl(ttlMs),
      repeatCount: 1
    };
    const next = [notification, ...current].slice(0, this.maxVisible);

    // Remove overflowed notifications and clear their timers.
    for (const dropped of current) {
      if (!next.some((n) => n.id === dropped.id)) {
        this.clearTimer(dropped.id);
        this.dueAtById.delete(dropped.id);
        this.remainingMsById.delete(dropped.id);
      }
    }

    this.notificationsSubject.next(next);
    this.startTimer(notification.id, notification.ttlMs);
  }

  private startTimer(id: string, ms: number): void {
    this.clearTimer(id);
    this.dueAtById.set(id, Date.now() + ms);
    const timeout = setTimeout(() => this.remove(id), ms);
    this.timeoutById.set(id, timeout);
  }

  private clearTimer(id: string): void {
    const timeout = this.timeoutById.get(id);
    if (!timeout) return;
    clearTimeout(timeout);
    this.timeoutById.delete(id);
  }

  private resolveTtl(ttlMs?: number): number {
    const raw = Number(ttlMs);
    if (!Number.isFinite(raw) || raw <= 0) return this.defaultTtlMs;
    return Math.max(1000, Math.min(15000, Math.round(raw)));
  }

  private newId(): string {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
}
